import { getAllArtifacts } from '@/artifacts';
import type { GeoMarkupStore } from '../index';

// ===== TYPES =====

/**
 * A currently-open form, reduced to what the guard needs: whether it holds
 * unsaved user input and how to close it. Produced by {@link getOpenForms}.
 */
interface OpenForm {
  isDirty: boolean;
  close: () => void;
}

// ===== HELPER =====

/**
 * Enumerate every currently-open form across the app — map artifacts
 * (task / note / obstruction) AND the non-artifact share config form.
 *
 * This is the single source of truth for "what forms are open and are they
 * dirty", consumed by the universal {@link FormConflictSlice.request} guard.
 * New forms participate automatically by either implementing the artifact
 * slice contract or being added here (share form).
 *
 * Notes report `formIsDirty: false` by design — the note form auto-saves on
 * every keystroke, so closing it never loses data and must not prompt.
 */
function getOpenForms(store: GeoMarkupStore): OpenForm[] {
  const forms: OpenForm[] = [];

  for (const artifact of getAllArtifacts()) {
    const slice = artifact.getSlice(store);
    if (slice.formIsOpen) {
      forms.push({ isDirty: slice.formIsDirty, close: slice.closeForm });
    }
  }

  if (store.shareForm.formIsOpen) {
    forms.push({
      isDirty: store.shareForm.formIsDirty,
      close: store.shareForm.closeForm,
    });
  }

  return forms;
}

// ===== SLICE INTERFACE =====

export interface FormConflictSlice {
  formConflict: {
    /** Whether the unsaved-changes confirmation dialog is visible. */
    showDialog: boolean;
    /** Continuation to run if the user confirms discarding changes. */
    pendingOnConfirm: (() => void) | null;

    /**
     * Universal unsaved-changes guard. Used for every action that would
     * discard an open form: closing it, opening another form, or switching
     * workspace.
     *
     * - If no open form is dirty: closes all open forms and runs `onProceed`
     *   immediately.
     * - If any open form is dirty: shows ONE confirmation dialog. On confirm
     *   (`confirmSwitch`) it closes all open forms and runs `onProceed`; on
     *   cancel (`cancelSwitch`) nothing changes and the forms stay open.
     */
    request: (onProceed: () => void) => void;
    confirmSwitch: () => void;
    cancelSwitch: () => void;
  };
}

// ===== SLICE CREATOR =====

export const createFormConflictSlice = (
  set: (fn: (state: FormConflictSlice) => void) => void,
  get: () => FormConflictSlice,
  _store: unknown
) => ({
  formConflict: {
    showDialog: false,
    pendingOnConfirm: null as (() => void) | null,

    request: (onProceed: () => void) => {
      const store = get() as unknown as GeoMarkupStore;
      const openForms = getOpenForms(store);

      const proceed = () => {
        openForms.forEach((form) => form.close());
        onProceed();
      };

      if (!openForms.some((form) => form.isDirty)) {
        // Clean (or no) forms: close them and proceed without prompting.
        proceed();
        return;
      }

      set((state) => {
        state.formConflict.showDialog = true;
        state.formConflict.pendingOnConfirm = proceed;
      });
    },

    confirmSwitch: () => {
      const pending = (get() as unknown as GeoMarkupStore).formConflict
        .pendingOnConfirm;
      set((state) => {
        state.formConflict.showDialog = false;
        state.formConflict.pendingOnConfirm = null;
      });
      pending?.();
    },

    cancelSwitch: () => {
      set((state) => {
        state.formConflict.showDialog = false;
        state.formConflict.pendingOnConfirm = null;
      });
    },
  },
});
